import React, { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ChevronDownIcon } from "lucide-react";
import { CtaButtons } from "./CtaButtons";
import { TitleUnderline } from "./TitleUnderline";
import { images } from "../data/content";
export function Hero() {
  const sectionRef = useRef<HTMLElement>(null);
  const reduce = useReducedMotion();
  const [atTop, setAtTop] = useState(true);
  useEffect(() => {
    const onScroll = () => setAtTop(window.scrollY < 40);
    onScroll();
    window.addEventListener("scroll", onScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  const scrollToNext = () => {
    const next = sectionRef.current?.nextElementSibling as HTMLElement | null;
    if (!next) return;
    const headerHeight = document.querySelector("header")?.getBoundingClientRect().height ?? 0;
    const top = next.getBoundingClientRect().top + window.scrollY - headerHeight;
    window.scrollTo({
      top,
      behavior: reduce ? "auto" : "smooth"
    });
  };
  return <section ref={sectionRef} id="top" className="relative flex h-[calc(96svh-3.5rem)] min-h-[520px] w-full items-center overflow-hidden bg-shade md:h-[calc(96svh-4rem)]" data-id="element-151" data-mp="3j6oe7qokeyt9">
      <img src={images.hero} alt="Eva-Maria Bast schreibt in ein Notizbuch, umgeben von Zitronenbäumen" className="absolute inset-0 h-full w-full object-cover object-[72%_65%]" data-id="element-152" data-mp="39cpboqkkxd0s" />
      {/* Mobile: text spans the full width, so the whole photo stays dark. */}
      <div className="absolute inset-0 bg-shade/70 md:hidden" aria-hidden="true" data-id="element-153" data-mp="35aubxqiar7p4" />
      {/* Tablet/desktop: dark behind the text on the left, fading lighter over Eva-Maria on the right (requested by the client). */}
      <div className="absolute inset-0 hidden md:block" aria-hidden="true" style={{
      background: "linear-gradient(90deg, rgba(42,44,48,0.8) 0%, rgba(42,44,48,0.7) 40%, rgba(42,44,48,0.45) 62%, rgba(42,44,48,0.22) 100%)"
    }} data-id="element-154" data-mp="2hj5fbxyfzx2d" />

      <motion.div initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.3,
      ease: [0.23, 1, 0.32, 1]
    }} className="relative mx-auto w-full max-w-7xl px-5 pt-8 md:px-10 md:pt-0" data-id="element-155" data-mp="s44ejdlhv3d5">
        <div className="max-w-2xl" data-id="element-156" data-mp="de6oes4083lb">
          <h1 className="font-serif text-[clamp(2rem,10.5vw,2.75rem)] font-medium leading-[1.02] text-cream sm:text-6xl lg:text-[clamp(3.25rem,9vh,5.5rem)]" data-id="element-157" data-mp="deyh9v67vn7w">
            Dein <TitleUnderline suffix="." data-id="element-158" data-mp="162ugb6j9dr7u">Buch</TitleUnderline>
            <br data-id="element-159" data-mp="112pud0zl04a8" />
            <span className="whitespace-nowrap" data-id="element-160" data-mp="1126o4hjan4c1">Fertig geschrieben.</span>
            <br data-id="element-161" data-mp="114kg2hiog0fd" />
            <em className="font-normal" data-id="element-162" data-mp="x1ecjhmy8ncl">In Deinen Händen.</em>
          </h1>
          <p className="mt-4 max-w-xl text-base font-light leading-relaxed text-cream/90 md:mt-6 md:text-lg" data-id="element-163" data-mp="x3h1dycd8afq">
            Du trägst eine Geschichte in Dir – und den Traum, sie eines Tages als Buch zu sehen. Mit meiner
            FlowKus-Methode schreibst und überarbeitest Du Dein Manuskript in einem halben Jahr – mit dem Handwerk
            einer achtfachen SPIEGEL-Bestseller-Autorin und dem Mindset, das Dich wirklich dranbleiben lässt.
          </p>
          <CtaButtons className="mt-6 md:mt-10" data-id="element-164" data-mp="2697m4gcskvx2" />

          {/* Mobile: just the arrow, centred under the buttons. */}
          <button type="button" onClick={scrollToNext} aria-label="Nach unten scrollen" className="mx-auto mt-3 flex rounded-full p-1 text-cream/80 transition-colors duration-150 hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-cream/60 md:hidden" data-id="element-165" data-mp="2b20s17jzyaaq">
            <motion.span animate={reduce ? undefined : {
            y: [0, 4, 0]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: "easeInOut"
          }} aria-hidden="true" data-id="element-166" data-mp="2tzbpcmm2itnj">
              <ChevronDownIcon className="h-7 w-7" strokeWidth={1.25} data-id="element-167" data-mp="zl12gjp1a9ke" />
            </motion.span>
          </button>
        </div>
      </motion.div>

      {!reduce && <motion.div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-2.5 origin-bottom bg-cream-deep md:h-3" initial={{
      scaleY: 0
    }} animate={atTop ? {
      scaleY: [0, 1, 0]
    } : {
      scaleY: 0
    }} transition={atTop ? {
      duration: 2,
      repeat: Infinity,
      repeatDelay: 1.2,
      ease: [0.45, 0, 0.55, 1]
    } : {
      duration: 0.2
    }} data-id="element-168" data-mp="4d73uw8q0vx5" />}

      <motion.button type="button" onClick={scrollToNext} initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} transition={{
      duration: 0.3,
      delay: 0.6
    }} aria-label="Nach unten scrollen" className="group absolute bottom-8 right-5 hidden flex-col items-center gap-1.5 rounded-full px-3 py-2 text-cream/80 transition-colors duration-150 hover:text-cream focus:outline-none focus-visible:ring-2 focus-visible:ring-cream/60 md:bottom-10 md:right-16 md:flex lg:right-24" data-id="element-169" data-mp="3csil05l3fd22">
        <span className="text-[13px] uppercase tracking-[0.3em]" data-id="element-170" data-mp="19gkn521fo5ni">Weiterlesen</span>
        <motion.span animate={reduce ? undefined : {
        y: [0, 4, 0]
      }} transition={{
        duration: 1.8,
        repeat: Infinity,
        ease: "easeInOut"
      }} aria-hidden="true" data-id="element-171" data-mp="1eaorddjbdzkw">
          <ChevronDownIcon className="h-7 w-7" strokeWidth={1.25} data-id="element-172" data-mp="14ju54962izzg" />
        </motion.span>
      </motion.button>
    </section>;
}